import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/config/api';
import { queryClient } from '@/config/queryClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CustomerUser {
  user_id: number;
  username: string;
  display_name?: string;
  role: string;
  permissions: string[];
  customer: {
    id: number;
    name: string;
    email: string;
    phone: string;
    credit_limit: string;
    portal_status: string;
    customer_type: string;
    pricing_tier: string;
  } | null;
}

interface AuthState {
  user: CustomerUser | null;
  isAuthenticated: boolean;

  login: (user: CustomerUser) => void;
  logout: () => void;
  updateUser: (user: Partial<CustomerUser>) => void;
}

// ---------------------------------------------------------------------------
// Store
//
// JWT tokens are now stored in httpOnly cookies set by the server.
// localStorage is only used for non-sensitive user profile info so that
// the UI can render immediately on page load without an extra /auth/me call.
// ---------------------------------------------------------------------------

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,

      login: (user: CustomerUser) => {
        // Token is handled via httpOnly cookie — no localStorage for tokens
        set({
          user,
          isAuthenticated: true,
        });
      },

      logout: () => {
        // Tell the server to clear the httpOnly cookie
        api.post('/auth/logout').catch(() => {});
        queryClient.clear();
        set({
          user: null,
          isAuthenticated: false,
        });
      },

      updateUser: (partial: Partial<CustomerUser>) => {
        const current = get().user;
        if (current) {
          set({ user: { ...current, ...partial } });
        }
      },
    }),
    {
      name: 'freshops-portal-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
